import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { folderStanding } from '@/features/onedrive/folder-coverage.js';
import {
  removeFolderFromList,
  removePlaceFromList,
} from '@/features/microsoft/remove-from-list.js';
import { findOneDriveKnowledgeSource } from '@/features/onedrive/onedrive-knowledge.js';
import { OneDriveFolderBrowser } from '@/features/onedrive/OneDriveFolderBrowser.js';
import { useWorkspaceSharing } from '@/features/workspace/useWorkspaceSharing.js';
import {
  knownPersonal,
  type WorkspaceSharing,
} from '@/features/workspace/workspace-access.js';
import * as connectorsApi from '@/integration/services/integration-api.js';
import { OneDriveScopeList } from '@/features/onedrive/OneDriveScopeList.js';
import {
  createOneDriveKnowledgeSource,
  type OneDriveScope,
} from '@/lib/api/onedrive.js';
import {
  oneDriveAccountQueryOptions,
  oneDriveKeys,
  oneDriveScopeBudgetQueryOptions,
  updateOneDriveScope,
} from '@/lib/api/onedrive.js';
import {
  setSharePointPlacePolicy,
  sharePointPlaceKeys,
  sharePointPlacesQueryOptions,
  updateSharePointPlace,
  type SharePointPlace,
} from '@/lib/api/sharepoint.js';
import {
  workspaceConnectorsQueryOptions,
  type WorkspaceConnectorConnection,
} from '@/lib/api/workspace-connectors.js';
import { statusBadge } from '@/features/workspace/knowledge/source-status.js';
import {
  createKnowledgeSource,
  deleteKnowledgeSource,
  knowledgeSourceKeys,
  knowledgeSourcesQueryOptions,
  type KnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';
import { workspaceQueryOptions } from '@/lib/api/workspaces.js';
import { notify } from '@/lib/notify.js';

import {
  findPlaceKnowledgeSource,
  placeIndexStanding,
  placeKnowledgeAddress,
} from './place-index.js';
import {
  SharePointPlaceBrowser,
  type BrowsePosition,
} from './SharePointPlaceBrowser.js';
import {
  levelCover,
  placeStanding,
  type RowStanding,
} from './place-coverage.js';
import { dismissNothingSelected } from './nothing-selected-dismissal.js';
import {
  ownerReachesNothing,
  reachName,
  useSelectedFolderCount,
} from './selected-folders.js';
import { SharePointPlaceList } from './SharePointPlaceList.js';
import { SharePointRestrictDialog } from './SharePointRestrictDialog.js';

/**
 * Everything one Microsoft connection exposes to one workspace, in one place.
 *
 * SharePoint and OneDrive are two tabs rather than two screens because they are two halves of the
 * same connection: `onedrive_accounts` is keyed by the SharePoint connection id, so a person asking
 * "what can the agent reach through this connection here" is asking one question. Splitting the
 * answer across the Artifacts section's two tabs made them look like two connectors.
 *
 * The dialog manages the list; it does not browse it. That is why the OneDrive rows are handed no
 * way to open a folder — there is nowhere in a dialog to open one into — and why adding still opens
 * the pickers that already know how to walk each tree.
 */
/**
 * What an unrestricted SharePoint reaches. A personal workspace is open on purpose — nobody else is
 * in it — so it says what is open and what is not, instead of "not restricted yet", which reads as a
 * step still to take.
 */
function openSharePointDescription(
  sharing: WorkspaceSharing,
  wholeOneDrive: boolean | null,
): string {
  if (sharing === 'unknown') {
    return 'SharePoint is not restricted, so the agent reaches all of it.';
  }
  return sharing === 'personal'
    ? `In your personal workspace the agent reaches all of ${reachName(wholeOneDrive)}, in chat and in automations. Nothing goes into the knowledge base until you add it.`
    : 'SharePoint is not restricted yet, so the agent reaches all of it. OneDrive reaches only what is on its list.';
}

export function SelectFoldersDialog({
  open,
  onOpenChange,
  workspaceId,
  connectionId,
  connectionLabel,
  onConnectAnother,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** The connection this dialog configures. Its OneDrive half is filtered to it by id. */
  connectionId: string;
  /** What to call it in the footer, from the card that opened this. */
  connectionLabel: string;
  /**
   * Starts adding a SECOND connection of this provider, which belongs to whoever opened this dialog:
   * that flow names the connection first and tells its poll what was already on screen, and neither
   * is something this dialog knows. Left out by a surface with no such flow, and the footer then
   * offers nothing rather than a button that cannot work.
   */
  onConnectAnother?: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [restrictOpen, setRestrictOpen] = useState(false);
  const [allowingAll, setAllowingAll] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const sharing = useWorkspaceSharing(workspaceId);
  /**
   * Renaming is offered only in a shared workspace, the same rule the connector card follows: a
   * person holds ONE connection per provider in their own space, so there is nothing to tell apart
   * there and the name is the computed one. Two names for the same thing appear only where two
   * connections do.
   */
  const canRename = sharing !== 'personal';
  const workspaceQuery = useQuery(workspaceQueryOptions(workspaceId));
  /**
   * The workspace's SharePoint connections in the shape the add and restrict flows take. The card
   * that opens this dialog holds a different shape of the same row — the directory's presence entry —
   * so the shape those flows need is read here rather than converted at the boundary.
   */
  const connectorsQuery = useQuery(
    workspaceConnectorsQueryOptions(workspaceId),
  );
  const connections: WorkspaceConnectorConnection[] = (
    connectorsQuery.data ?? []
  )
    .filter((connector) => connector.provider === 'sharepoint')
    .flatMap((connector) => connector.connections);
  const connection = connections.find(
    (candidate) => candidate.connectionId === connectionId,
  );
  const placesQuery = useQuery(
    sharePointPlacesQueryOptions(workspaceId, connectionId),
  );
  const accountQuery = useQuery(
    oneDriveAccountQueryOptions(workspaceId, connectionId),
  );
  /**
   * Whether the caller's personal OneDrive hangs off THIS connection.
   *
   * A person has one OneDrive account, bound to exactly one SharePoint connection
   * (`onedrive_accounts.owner_user_id` is unique), while a workspace reaches several connections.
   * So on every other card there is no OneDrive behind the connection at all — and asking that
   * connection for a setup session is asking for a token it was never given, which is what the
   * service was refusing.
   */
  /** The drive behind THIS connection, asked for by connection rather than by person. */
  const oneDriveAccount = accountQuery.data ?? null;

  const sourcesQuery = useQuery(knowledgeSourcesQueryOptions(workspaceId));
  const foldersQuery = useQuery(oneDriveScopeBudgetQueryOptions(workspaceId));
  /** The open grant is the pair's reach, not a choice on the list — the list is what was added. */
  const folders = (foldersQuery.data?.scopes ?? []).filter(
    (scope) => scope.connectionId === connectionId && !scope.openGrant,
  );
  const folderCount = useSelectedFolderCount(workspaceId, connectionId);

  /**
   * Closing the list with nothing on it answers the red mark that led here: the owner looked, chose
   * nothing, and SharePoint stays off in this workspace. The mark stops asking here, and only here —
   * the same connection still asks in every other workspace it travels to.
   */
  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen && ownerReachesNothing(folderCount)) {
      dismissNothingSelected(workspaceId, connectionId);
    }
    onOpenChange(nextOpen);
  }

  /**
   * Both halves are refreshed after either one changes. A place and a folder are separate rows, but
   * the count in the title counts them together, so a change to one makes the other's total stale.
   * The knowledge shelf too: the knowledge switch writes to it, and a row reads its state from it.
   */
  /**
   * Opens the pair again: everything reachable, the lists back to the knowledge base only. The
   * OneDrive scopes are read again, which is what gives the drive its whole-drive grant.
   */
  async function allowAll(): Promise<void> {
    setAllowingAll(true);
    try {
      await setSharePointPlacePolicy(workspaceId, connectionId, false);
      await refresh();
    } catch (reason) {
      notify.error(
        reason instanceof Error ? reason.message : 'Could not open access.',
      );
    } finally {
      setAllowingAll(false);
    }
  }

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: sharePointPlaceKeys.policy(workspaceId, connectionId),
      }),
      queryClient.invalidateQueries({
        queryKey: oneDriveKeys.scopes(workspaceId),
      }),
      queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      }),
    ]);
  }

  /**
   * The source registered for a folder's own root, or nothing. A subfolder registered through the
   * knowledge picker is a different source at a different address, so the row must not read as if
   * the whole folder were on the shelf.
   */
  function folderSource(scope: OneDriveScope) {
    return findOneDriveKnowledgeSource(sourcesQuery.data ?? [], {
      scopeId: scope.scopeId,
      itemId: scope.rootItemId,
    });
  }

  /** The source registered at a place's own address, the SharePoint half of {@link folderSource}. */
  function placeSource(place: SharePointPlace) {
    return findPlaceKnowledgeSource(
      place,
      sourcesQuery.data ?? [],
      connectionId,
      connections,
    );
  }

  /**
   * Turning the workspace's search index on or off for one row.
   *
   * It is the one setting that is not part of the allow-list row itself — indexing lives in its own
   * registry — so it is two calls rather than a field on the update. Every level answers it: a site
   * and a library register as their own kinds of source, each anchored on an id it already carries.
   */
  async function setPlaceSearchable(
    place: SharePointPlace,
    next: boolean,
  ): Promise<void> {
    if (next) {
      const address = placeKnowledgeAddress(place);
      if (!address) return;
      await createKnowledgeSource(workspaceId, {
        type: address.type,
        enabled: true,
        config: {
          site_id: address.siteId,
          ...(address.driveId ? { drive_id: address.driveId } : {}),
          item_id: address.itemId,
          connection_id: connectionId,
          name: place.alias ?? place.nameSnapshot,
          ...(place.webUrlSnapshot ? { url: place.webUrlSnapshot } : {}),
        },
      });
    } else {
      const source = placeSource(place);
      if (source) await deleteKnowledgeSource(workspaceId, source.id);
    }
    await refresh();
  }

  async function setScopeSearchable(
    scope: OneDriveScope,
    next: boolean,
  ): Promise<void> {
    if (next) {
      /**
       * A source paused under the old rule — automation access off used to pause a folder's
       * knowledge — has nothing left that will resume it. Replacing it with a fresh one is the only
       * resume open from here, and it indexes the folder again from the start.
       */
      const paused = folderSource(scope);
      if (paused) await deleteKnowledgeSource(workspaceId, paused.id);
      await createOneDriveKnowledgeSource(scope.scopeId, {
        workspaceId,
        itemId: scope.rootItemId,
      });
    } else {
      const source = folderSource(scope);
      if (source) await deleteKnowledgeSource(workspaceId, source.id);
    }
    await refresh();
  }

  /**
   * Whether a row the browser is showing is already on the list. Every level is a place, a single
   * file included, so the places alone answer it — a file that is only in the knowledge base, from
   * before a file could be a place, still offers Add and becomes one.
   */
  function rowStanding(
    row: { kind: 'site' | 'library' | 'folder' | 'file'; id: string },
    position: BrowsePosition,
  ): RowStanding {
    return placeStanding(placesQuery.data?.places ?? [], row, position);
  }

  /** One row off the list, and its own knowledge source with it — see {@link removePlaceFromList}. */
  async function removePlace(placeId: string): Promise<void> {
    await removePlaceFromList(queryClient, {
      workspaceId,
      connectionId,
      connections,
      placeId,
      places: placesQuery.data?.places ?? [],
    });
  }

  /** The OneDrive half of {@link removePlace}. */
  async function removeScope(scopeId: string): Promise<void> {
    await removeFolderFromList(queryClient, {
      workspaceId,
      scopeId,
    });
  }

  /**
   * Takes places off the list so a wider one can be added over them.
   *
   * Removals run one after another rather than together: identity locks the connection's place set
   * for each write, so firing them at once would have them queue behind that lock anyway — and a
   * failure halfway is easier to describe when the ones before it are known to have finished.
   */
  async function replacePlaces(placeIds: readonly string[]): Promise<void> {
    for (const placeId of placeIds) {
      await removePlace(placeId);
    }
    await refresh();
  }

  /** The OneDrive half of {@link replacePlaces}. */
  async function replaceScopes(scopeIds: readonly string[]): Promise<void> {
    for (const scopeId of scopeIds) {
      await removeScope(scopeId);
    }
    await refresh();
  }

  /** Writes the new name, then refreshes whatever reads it — the card behind this dialog included. */
  async function rename(): Promise<void> {
    const next = (renaming ?? '').trim();
    if (next.length === 0) return;
    setRenameBusy(true);
    try {
      await connectorsApi.updateConnectionProfile(
        'sharepoint',
        connectionId,
        { label: next },
        workspaceId,
      );
      await queryClient.invalidateQueries({ queryKey: ['connections'] });
      setRenaming(null);
    } catch (reason) {
      notify.error(
        reason instanceof Error ? reason.message : 'Could not rename this.',
      );
    } finally {
      setRenameBusy(false);
    }
  }

  const selected = (placesQuery.data?.places.length ?? 0) + folders.length;
  /**
   * Each ceiling belongs to the list it binds, and the two are never added together: they are
   * separate budgets, so their sum is a number nobody can reach in either direction.
   */
  const placesLimit = placesQuery.data?.placesLimit ?? 0;
  const foldersLimit = oneDriveAccount
    ? (foldersQuery.data?.scopesLimit ?? 0)
    : 0;
  const workspaceName = workspaceQuery.data?.name;
  /** Everything reachable, because this is the owner's own personal workspace — how it starts. */
  const openByDesign =
    sharing === 'personal' && placesQuery.data?.enforced === false;
  /**
   * The OneDrive list is knowledge-only only while the whole drive is confirmed reached. Without a
   * confirmed grant the listed folders may still decide access, so their levels stay.
   */
  const oneDriveOpen = openByDesign && folderCount.wholeOneDrive === true;

  /**
   * How a row's indexing is going, in the Knowledge tab's own words, or nothing when the indexer has
   * not answered. The switch says whether the row is on the shelf; this says whether that has
   * happened yet, which is the other half of the question somebody opens the gear with.
   */
  function indexStatusOf(source: KnowledgeSource | undefined): string | null {
    if (!source) return null;
    return statusBadge(source)?.label ?? null;
  }

  /**
   * `Selected items`, and nothing about room until there is none. The ceiling is a safety
   * limit nobody adding by hand should meet, so a running count was a number to worry about for no
   * reason; it is said only once the list is full, which is when the next add would be refused.
   */
  function listHeading(noun: string, count: number, ceiling: number): string {
    return ceiling > 0 && count >= ceiling
      ? `Selected ${noun} (full: ${ceiling})`
      : `Selected ${noun}`;
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      /*
        Not "folders": SharePoint takes a site, a document library, a folder or a single file, and
        OneDrive a folder or a single file. The title says what the dialog decides instead of
        naming one of the things it decides it about.
      */
      title={
        workspaceName
          ? `What the agent reaches in ${workspaceName}`
          : 'What the agent reaches in this workspace'
      }
      description={
        placesQuery.data?.enforced === false
          ? openSharePointDescription(sharing, folderCount.wholeOneDrive)
          : `The agent reaches only what is on these lists. ${selected} ${selected === 1 ? 'item' : 'items'} selected.`
      }
      /*
        `overflow-y-hidden` is load-bearing, and the axis matters: DialogContent carries
        `overflow-y-auto`, and tailwind-merge treats `overflow` and `overflow-y` as different
        groups — so a plain `overflow-hidden` is kept ALONGSIDE it and the container still scrolls.
        A scrolling container gives its children no bounded height to divide, so the flexible tree
        grew to its content and painted over the footer. Scrolling belongs to the regions inside.
      */
      /*
        A DEFINITE height, not a maximum. Sized to its content the dialog resized on every step —
        open a site with one library and the whole window jumped smaller, then bigger again on the
        way back. Nothing in here is worth moving the frame for, so the frame stops moving.
      */
      className="flex h-[min(92vh,52rem)] max-h-[92vh] w-full max-w-[var(--dialog-w-lg)] flex-col overflow-y-hidden"
    >
      {/*
        One mode for both tabs: one Microsoft connection carries SharePoint and OneDrive, and one
        policy decides both. Only in the owner's own personal workspace, the one place a pair may be
        open, and only for the owner, who is the one who may change it.
      */}
      {sharing === 'personal' && placesQuery.data?.isOwner === true && (
        <div className="mt-3 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
          <p className="text-sm font-medium">
            {openByDesign
              ? `All of ${reachName(folderCount.wholeOneDrive)}`
              : 'Only the items on these lists'}
          </p>
          {openByDesign ? (
            <Button size="sm" onClick={() => setRestrictOpen(true)}>
              <ShieldIcon /> Restrict access
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={allowingAll}
              onClick={() => void allowAll()}
            >
              Allow all
            </Button>
          )}
        </div>
      )}
      <Tabs
        defaultValue="sharepoint"
        className="mt-3 flex min-h-0 flex-1 flex-col gap-3"
      >
        <TabsList>
          <TabsTrigger value="sharepoint">SharePoint</TabsTrigger>
          <TabsTrigger value="onedrive">OneDrive</TabsTrigger>
        </TabsList>

        {/*
          Side by side once the dialog is wide enough, stacked below that.

          The two regions were competing for one column of a 634px dialog, and the chosen one was
          capped at a flat 128px — clipped by the second place somebody added, while the tree under
          it had 212px to show seventy sites in. Side by side each gets the panel's full height
          instead of a slice of it. Stacked, the cap is a SHARE of the panel rather than a fixed
          number, so it uses whatever the screen actually has.

          Deliberately not resize-on-hover: it moves the row you were reaching for out from under
          the cursor, and it does not exist at all for a keyboard or a touch screen.
        */}
        <TabsContent
          value="sharepoint"
          className="flex min-h-0 flex-1 flex-col gap-4 xl:flex-row xl:gap-5"
        >
          {placesQuery.isLoading && (
            <Skeleton className="h-24 w-full rounded-xl" />
          )}
          {placesQuery.isError && (
            <p role="alert" className="text-sm text-destructive">
              Could not load this connection&rsquo;s allowed places.
            </p>
          )}
          {placesQuery.data && (
            /*
              Its own column, with a heading the same height as the one beside it, so the two lists
              start on one line and — because both boxes flex — end on one line too.
            */
            <section className="flex min-h-0 flex-col gap-2 max-xl:max-h-[40%] xl:w-[44%] xl:shrink xl:grow-0 xl:basis-[44%]">
              <h3 className="shrink-0 text-sm font-medium">
                {listHeading(
                  'items',
                  placesQuery.data.places.length,
                  placesLimit,
                )}
              </h3>
              <SharePointPlaceList
                compact
                enforced={placesQuery.data.enforced}
                personalWorkspace={knownPersonal(sharing)}
                wholeOneDrive={folderCount.wholeOneDrive}
                onAllowAll={() => void allowAll()}
                places={placesQuery.data.places}
                isOwner={placesQuery.data.isOwner}
                onRestrict={() => setRestrictOpen(true)}
                onUpdate={async (placeId, patch) => {
                  await updateSharePointPlace(placeId, {
                    workspaceId,
                    ...patch,
                  });
                  await refresh();
                }}
                onDelete={async (placeId) => {
                  await removePlace(placeId);
                  await refresh();
                }}
                {...(sourcesQuery.data && connectorsQuery.data
                  ? {
                      searchable: {
                        of: (place: SharePointPlace) =>
                          placeIndexStanding(
                            place,
                            sourcesQuery.data,
                            connectionId,
                            connections,
                          ) === 'on',
                        set: setPlaceSearchable,
                        statusOf: (place: SharePointPlace) =>
                          indexStatusOf(placeSource(place)),
                      },
                    }
                  : {})}
              />
            </section>
          )}

          {/*
            Only for the connection's owner: browsing opens a setup session, which the server
            grants the owner alone, so for anybody else this column could only show an error. The
            OneDrive tab below leaves its own column out on the same answer.
          */}
          {placesQuery.data?.isOwner === true && (
            <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
              <h3 className="shrink-0 text-sm font-medium">Available items</h3>
              <SharePointPlaceBrowser
                workspaceId={workspaceId}
                connectionId={connectionId}
                onAdded={() => void refresh()}
                standingOf={rowStanding}
                coverOf={(position) =>
                  levelCover(placesQuery.data?.places ?? [], position)
                }
                onReplace={replacePlaces}
              />
            </section>
          )}
        </TabsContent>

        <TabsContent
          value="onedrive"
          className="flex min-h-0 flex-1 flex-col gap-3 xl:flex-row xl:gap-5"
        >
          <section className="flex min-h-0 flex-col gap-2 max-xl:max-h-[40%] xl:w-[44%] xl:shrink xl:grow-0 xl:basis-[44%]">
            <h3 className="shrink-0 text-sm font-medium">
              {listHeading('items', folders.length, foldersLimit)}
            </h3>
            {oneDriveOpen && placesQuery.data?.isOwner === true && (
              <p className="shrink-0 text-sm text-muted-foreground">
                Add an item here only to put it in the knowledge base.
              </p>
            )}
            {folders.length > 0 && oneDriveAccount ? (
              <OneDriveScopeList
                compact
                openByDesign={oneDriveOpen}
                account={oneDriveAccount}
                scopes={folders}
                selectedScopeId={null}
                onUpdate={async (scopeId, patch) => {
                  await updateOneDriveScope(scopeId, {
                    workspaceId,
                    ...patch,
                  });
                  await refresh();
                }}
                onDelete={async (scopeId) => {
                  await removeScope(scopeId);
                  await refresh();
                }}
                {...(sourcesQuery.data
                  ? {
                      searchable: {
                        of: (scope: OneDriveScope) =>
                          folderSource(scope)?.enabled === true,
                        set: setScopeSearchable,
                        statusOf: (scope: OneDriveScope) =>
                          indexStatusOf(folderSource(scope)),
                      },
                    }
                  : {})}
              />
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-border p-2">
                <p className="px-3 py-2 text-center text-sm text-muted-foreground">
                  {placesQuery.data?.isOwner === true
                    ? 'Nothing yet. What you add on the right appears here.'
                    : 'Nothing has been added yet.'}
                </p>
              </div>
            )}
          </section>

          {/*
            Browsed regardless of whether an account exists yet, because browsing is what CREATES
            one: opening the drive is the step that verifies the account behind this connection and
            binds it. Waiting for an account first left a tab that said "add a folder" and offered
            nothing to add one with.

            Only for the connection's owner, though — browsing BINDS, and identity refuses a bind
            from anybody else. The same answer the SharePoint tab uses, so the two halves of one
            dialog cannot disagree about who may change this connection.
          */}
          {placesQuery.data?.isOwner === true ? (
            <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
              <h3 className="shrink-0 text-sm font-medium">Available items</h3>
              <OneDriveFolderBrowser
                workspaceId={workspaceId}
                connectionId={connectionId}
                onAdded={() => void refresh()}
                standingOf={(itemId, rowAncestors) =>
                  folderStanding(folders, itemId, rowAncestors)
                }
                onReplace={replaceScopes}
              />
            </section>
          ) : (
            <p className="shrink-0 text-sm text-muted-foreground">
              Only the person who connected this account can change what it
              exposes.
            </p>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-4 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground">
            You&rsquo;re using the {connectionLabel} connection.
          </p>
          {canRename && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRenaming(connectionLabel)}
            >
              Rename
            </Button>
          )}
          {/*
            Handed back to whoever opened this dialog rather than run here.

            Connecting a SECOND connection is not one call: the new one has to be named before it
            leaves, or the service refuses it, and the poll that waits for it has to be told which
            connections were already on screen — without that list it matches one of them on its
            first tick and closes the sign-in window before anybody has consented. This button did
            neither, so it opened a popup and shut it again, and nothing was ever connected.

            The card that owns this dialog already runs that flow correctly. One copy of it.
          */}
          {onConnectAnother && (
            <Button variant="ghost" size="sm" onClick={onConnectAnother}>
              Connect another SharePoint
            </Button>
          )}
        </div>
        <Button
          variant="brand"
          size="sm"
          onClick={() => handleOpenChange(false)}
        >
          Done
        </Button>
      </div>

      <AppDialog
        open={renaming !== null}
        onOpenChange={(next) => !next && setRenaming(null)}
        title="Rename this connection"
        description="The name tells this connection apart from the others this workspace reaches. It changes nothing about what the connection can do."
      >
        <div className="flex flex-col gap-4 pt-3">
          <Input
            aria-label="Connection name"
            value={renaming ?? ''}
            disabled={renameBusy}
            onChange={(event) => setRenaming(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              disabled={renameBusy}
              onClick={() => setRenaming(null)}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              disabled={renameBusy || (renaming ?? '').trim().length === 0}
              onClick={() => void rename()}
            >
              {renameBusy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </AppDialog>

      <SharePointRestrictDialog
        personalWorkspace={sharing === 'personal'}
        open={restrictOpen}
        workspaceId={workspaceId}
        connectionId={connectionId}
        places={placesQuery.data?.places ?? []}
        connections={connections}
        onOpenChange={setRestrictOpen}
        onRestricted={() => void refresh()}
      />
    </AppDialog>
  );
}
